//src/components/EditPropertyImageUploader.jsx
import { useCallback, useRef } from 'react';
import { MdAdd, MdClose, MdStar, MdStarBorder } from 'react-icons/md';

const EditPropertyImageUploader = ({ images = [], onChange, error, disabled = false, maxFiles = 10 }) => {
    const inputRef = useRef(null);

    const activeImages = images.filter((img) => !img.toDelete);
    const deletedImages = images.filter((img) => img.toDelete && img.isExisting);

    const handleFiles = useCallback(
        (files) => {
            const currentActive = images.filter((img) => !img.toDelete);
            const remaining = maxFiles - currentActive.length;
            if (remaining <= 0) return;

            const toAdd = Array.from(files).slice(0, remaining);
            const newImages = toAdd.map((file) => ({
                file,
                preview: URL.createObjectURL(file),
                isExisting: false,
                toDelete: false,
            }));

            onChange([...images, ...newImages]);
        },
        [images, onChange, maxFiles]
    );

    const handleInputChange = (e) => {
        if (e.target.files?.length) {
            handleFiles(e.target.files);
            e.target.value = '';
        }
    };

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            if (disabled) return;
            if (e.dataTransfer.files?.length) {
                handleFiles(e.dataTransfer.files);
            }
        },
        [disabled, handleFiles]
    );

    const handleRemove = (imgToRemove) => {
        if (imgToRemove.isExisting) {
            // Mark existing as toDelete instead of removing
            onChange(images.map((img) => (img === imgToRemove ? { ...img, toDelete: true } : img)));
        } else {
            // Remove new uploads entirely
            URL.revokeObjectURL(imgToRemove.preview);
            onChange(images.filter((img) => img !== imgToRemove));
        }
    };

    const handleRestore = (imgToRestore) => {
        const currentActive = images.filter((img) => !img.toDelete);
        if (currentActive.length >= maxFiles) return;
        onChange(images.map((img) => (img === imgToRestore ? { ...img, toDelete: false } : img)));
    };

    const handleSetPrimary = (imgToPromote) => {
        // Move the selected image to position 0 among active images
        const active = images.filter((img) => !img.toDelete);
        const deleted = images.filter((img) => img.toDelete);

        const reordered = [imgToPromote, ...active.filter((img) => img !== imgToPromote), ...deleted];
        onChange(reordered);
    };

    const isDragOver = useRef(false);

    const activeCount = activeImages.length;
    const canAddMore = activeCount < maxFiles && !disabled;

    return (
        <div className="sell-option-group">
            <label className="mp-label">
                Foto Properti
                <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: 8, fontSize: 13 }}>
                    (Maks. {maxFiles} foto)
                </span>
            </label>
            <p className="sell-spec-hint">
                Foto pertama akan menjadi foto utama. Hapus foto yang tidak diinginkan dan tambah foto baru jika perlu.
            </p>

            {/* Active images grid */}
            {activeImages.length > 0 && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    {activeImages.map((img, index) => (
                        <div
                            key={img.id ?? img.preview}
                            style={{
                                position: 'relative',
                                borderRadius: 12,
                                overflow: 'hidden',
                                border:
                                    index === 0
                                        ? '2.5px solid var(--color-primary)'
                                        : '1.5px solid var(--color-border)',
                                background: 'var(--color-bg-soft)',
                                aspectRatio: '4/3',
                            }}
                        >
                            <img
                                src={img.preview}
                                alt={`Foto ${index + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />

                            {/* Primary badge */}
                            {index === 0 && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: 6,
                                        left: 6,
                                        background: 'var(--color-primary)',
                                        color: '#fff',
                                        borderRadius: 8,
                                        padding: '2px 8px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 3,
                                    }}
                                >
                                    <MdStar size={12} /> Utama
                                </div>
                            )}

                            {/* Existing badge */}
                            {img.isExisting && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 6,
                                        left: 6,
                                        background: 'rgba(0,0,0,0.55)',
                                        color: '#fff',
                                        borderRadius: 6,
                                        padding: '1px 6px',
                                        fontSize: 10,
                                        fontWeight: 600,
                                    }}
                                >
                                    Tersimpan
                                </div>
                            )}

                            {/* Action buttons */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 6,
                                    right: 6,
                                    display: 'flex',
                                    gap: 4,
                                }}
                            >
                                {index !== 0 && (
                                    <button
                                        type="button"
                                        title="Jadikan foto utama"
                                        onClick={() => handleSetPrimary(img)}
                                        style={{
                                            background: 'rgba(255,255,255,0.92)',
                                            border: 'none',
                                            borderRadius: 6,
                                            width: 28,
                                            height: 28,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            color: 'var(--color-primary)',
                                        }}
                                    >
                                        <MdStarBorder size={16} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    title="Hapus foto"
                                    onClick={() => handleRemove(img)}
                                    disabled={disabled}
                                    style={{
                                        background: 'rgba(239,68,68,0.9)',
                                        border: 'none',
                                        borderRadius: 6,
                                        width: 28,
                                        height: 28,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        color: '#fff',
                                    }}
                                >
                                    <MdClose size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Drop zone / Add more */}
            {canAddMore && (
                <div
                    onDrop={handleDrop}
                    onDragOver={(e) => {
                        e.preventDefault();
                    }}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        border: '2px dashed var(--color-border)',
                        borderRadius: 16,
                        padding: '20px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: 'rgba(255,255,255,0.55)',
                        color: 'var(--color-text-muted)',
                        transition: 'border-color 0.15s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <MdAdd size={24} color="var(--color-primary)" />
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Tambah Foto</span>
                    <span style={{ fontSize: 12 }}>
                        Klik atau seret foto ke sini • {activeCount}/{maxFiles} foto
                    </span>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpg,image/jpeg,image/png,image/webp"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleInputChange}
                        disabled={disabled}
                    />
                </div>
            )}

            {/* Deleted images (restore option) */}
            {deletedImages.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 8, fontWeight: 600 }}>
                        Foto yang akan dihapus ({deletedImages.length}):
                    </div>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                            gap: 8,
                        }}
                    >
                        {deletedImages.map((img) => (
                            <div
                                key={img.id}
                                style={{
                                    position: 'relative',
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                    border: '1.5px dashed var(--color-border)',
                                    opacity: 0.5,
                                    aspectRatio: '4/3',
                                }}
                            >
                                <img
                                    src={img.preview}
                                    alt="Akan dihapus"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'rgba(239,68,68,0.18)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={() => handleRestore(img)}
                                        style={{
                                            background: 'rgba(255,255,255,0.95)',
                                            border: 'none',
                                            borderRadius: 6,
                                            padding: '3px 10px',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            color: 'var(--color-primary)',
                                        }}
                                    >
                                        Batalkan
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {error && (
                <span className="sell-field-error" style={{ marginTop: 8, display: 'block' }}>
                    {error}
                </span>
            )}
        </div>
    );
};

export default EditPropertyImageUploader;
